import React, { useState } from "react";
import { X, Mail, Shield } from "lucide-react";
import Field from "./components/Field";
import RoleOption from "./components/RoleOption";
import FormFooter from "./components/FormFooter";
import { InvitationData, InvitationFormProps } from "./types/types";
import { inputCls } from "./utils/utils";

const InvitationForm: React.FC<InvitationFormProps> = ({
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<InvitationData>({
    email: "",
    role: "ROLE_USER",
    nom: "",
  });

  const [errors, setErrors] = useState<Partial<Record<keyof InvitationData, string>>>(
    {}
  );

  const validate = (): boolean => {
    const e: Partial<Record<keyof InvitationData, string>> = {};

    if (!formData.nom.trim()) e.nom = "Le nom est requis";

    if (!formData.email.trim()) {
      e.email = "L'email est requis";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      e.email = "Format d'email invalide";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!validate()) return;

    await onSubmit(formData);
  };

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
      onClick={onCancel} 
    >
      <div
        className="bg-white rounded-2xl w-full max-w-md overflow-hidden
        shadow-[0_24px_64px_rgba(0,0,0,0.18),0_0_0_1px_rgba(0,0,0,0.06)]
        animate-[gbpFadeIn_0.22s_ease-out]"
        onClick={(e) => e.stopPropagation()}  
      >
        <div className="h-1 w-full bg-gradient-to-r from-[#0F52BA] via-blue-400 to-blue-200" />

         <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-blue-50 text-[#0F52BA]">
            <Mail size={17} />
          </div>

          <div className="flex-1 min-w-0">
            <h2 className="text-[15px] font-bold text-slate-800">
              Inviter un utilisateur
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              Un lien d'activation sera envoyé par email.
            </p>
          </div>

          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-100
              text-slate-400 hover:bg-rose-50 hover:text-rose-500 hover:border-rose-100
              transition-all duration-150 disabled:opacity-50"
          >
            <X size={14} />
          </button>
        </div>

         <form
          onSubmit={handleSubmit}
          className="px-6 py-5 flex flex-col gap-5"
        >
<Field id="nom" label="Nom">
  <div className="flex flex-col gap-1">
    <input
      type="text"
      value={formData.nom}
      onChange={(e) =>
        setFormData((p) => ({
          ...p,
          nom: e.target.value,
        }))
      }
      className={inputCls(false)}
      placeholder="Ex: John Doe"
    />

     <span className="text-sm text-yellow-600">
    Le nom doit être exactement le même que celui utilisé dans votre compte GBP.
    </span>
  </div>
</Field>
           <Field id="email" label="Adresse email" error={errors.email}>
            <div className="relative">
              <Mail
                size={13}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300"
              />
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => {
                  setFormData((p) => ({
                    ...p,
                    email: e.target.value,
                  }));
                  if (errors.email)
                    setErrors((p) => ({
                      ...p,
                      email: undefined,
                    }));
                }}
                disabled={isLoading}
                placeholder="exemple@domaine.com"
                className={`pl-9 ${inputCls(!!errors.email)}`}
              />
            </div>
          </Field>

           <Field id="role" label="Rôle attribué">
            <div className="flex flex-col gap-2">
              <RoleOption
                value="ROLE_USER"
                label="Utilisateur"
                description="Accès standard à la gestion des fiches"
                icon={<Mail size={14} />}
                selected={formData.role === "ROLE_USER"}
                onSelect={() =>
                  setFormData((p) => ({
                    ...p,
                    role: "ROLE_USER",
                  }))
                }
              />

              <RoleOption
                value="ROLE_ADMIN"
                label="Administrateur"
                description="Accès complet, gestion des utilisateurs"
                icon={<Shield size={14} />}
                selected={formData.role === "ROLE_ADMIN"}
                onSelect={() =>
                  setFormData((p) => ({
                    ...p,
                    role: "ROLE_ADMIN",
                  }))
                }
              />

            </div>
          </Field>

          <FormFooter onCancel={onCancel} isLoading={isLoading} />
        </form>
      </div>
    </div>
  );
};

export default InvitationForm;